import { useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { sortArticles } from "../lib/articleOrder";
import { matchesCategory } from "../lib/categoryAliases";

export interface Article {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  category: string;
  category_color: string;
  author: string;
  date: string;
  read_time: string;
  image: string;
  featured: boolean;
  slug: string;
  tags: string[];
  subcategory?: string;
  views?: number;
  video_url?: string;
  created_at: string;
  updated_at: string;
  is_international?: boolean;
  additional_images?: string[];
  image_captions?: string[];
  /** Posición fijada manualmente desde el admin (1 = primera). null = orden por fecha. */
  position?: number | null;
  /** Lugar elegido al mover la noticia desde el admin sin fijarla. null = orden por fecha. */
  sort_at?: string | null;
}

/** Check if a category has aliases that need local filtering */
function hasCategoryAliases(category: string): boolean {
  // Quick check: if matchesCategory would expand this category, we need local filtering
  const testTags = ["educativo", "educativa", "educativos", "educacion"];
  return testTags.some((t) => matchesCategory(t, [], category));
}

export function useArticles(category?: string, featuredOnly?: boolean) {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("articles")
        .select("*")
        .order("created_at", { ascending: false });

      const needsLocalFilter = category && category !== "Todas" && hasCategoryAliases(category);

      if (category && category !== "Todas" && !needsLocalFilter) {
        // Support multi-category: match primary category OR tags array contains the category
        query = query.or(`category.eq."${category}",tags.cs.{"${category}"}`);
      }
      if (featuredOnly) {
        query = query.eq("featured", true);
      }

      const { data, error: supaError } = await query;

      if (supaError) throw supaError;
      // Normalize null fields to prevent runtime crashes
      let normalized = (data || []).map((a) => ({
        ...a,
        tags: Array.isArray(a.tags) ? a.tags : [],
        content: a.content || "",
        additional_images: Array.isArray(a.additional_images) ? a.additional_images : [],
        image_captions: Array.isArray(a.image_captions) ? a.image_captions : [],
      }));

      // Apply local alias filtering if needed
      if (category && category !== "Todas" && needsLocalFilter) {
        normalized = normalized.filter((a) =>
          matchesCategory(a.category || "", a.tags, category),
        );
      }

      // Última subida primero, luego las fijadas y el resto por fecha visible
      normalized = sortArticles(normalized);
      setArticles(normalized);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al cargar artículos");
    } finally {
      setLoading(false);
    }
  }, [category, featuredOnly]);

  useEffect(() => {
    fetchArticles();

    // Nombre de channel único para evitar conflictos entre múltiples instancias
    const channelName = category
      ? `articles-changes-${category.replace(/\s+/g, "-")}`
      : "articles-changes-all";

    const channel = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "articles" },
        (payload) => {
          // For INSERT, optimistically add if we can; otherwise refetch
          if (payload.eventType === "INSERT") {
            const newArticle = payload.new as Article;
            if (!newArticle) {
              fetchArticles();
              return;
            }
            // If filtering by category, check if article matches
            if (category && category !== "Todas") {
              const tags = Array.isArray(newArticle.tags) ? newArticle.tags : [];
              const matches =
                newArticle.category === category ||
                tags.includes(category) ||
                matchesCategory(newArticle.category || "", tags, category);
              if (!matches) return;
            }
            // If filtering by featured, skip unless featured
            if (featuredOnly && !newArticle.featured) return;

            setArticles((prev) => {
              // Avoid duplicates
              if (prev.some((a) => a.id === newArticle.id)) return prev;
              const updated = [
                {
                  ...newArticle,
                  tags: Array.isArray(newArticle.tags) ? newArticle.tags : [],
                  content: newArticle.content || "",
                  additional_images: Array.isArray(newArticle.additional_images) ? newArticle.additional_images : [],
                  image_captions: Array.isArray(newArticle.image_captions) ? newArticle.image_captions : [],
                },
                ...prev,
              ];
              return sortArticles(updated);
            });
          } else {
            // UPDATE or DELETE — refetch to stay consistent
            fetchArticles();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchArticles, category, featuredOnly]);

  return { articles, loading, error, refetch: fetchArticles };
}